import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card } from '../../components/ui/card';
import { resolveFileUrl } from '../../utils/fileUrl';
import { formatDisplayDate } from '../../lib/dateFormat';
import { toast } from 'sonner';
import PaginationBar from '../../components/PaginationBar';
import {
  Users, Search, CheckCircle, XCircle, Clock, ChevronDown, ChevronUp,
  Mail, Phone, MapPin, Shield, Heart, User, Loader2, MessageSquare,
  Camera, AlertTriangle, Filter, X, Upload, Download, FileText, Copy, Link, Edit
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORY_COLORS = {
  "Mahasabha Padadhikari": "bg-amber-100 text-amber-800",
  "Karyakarini Sadasya": "bg-amber-100 text-amber-800",
  "Samaj Adhyaksh": "bg-amber-100 text-amber-800",
  "Mahila Swayamsevak": "bg-purple-100 text-purple-800",
  "Swayamsevak": "bg-blue-100 text-blue-800",
  "Sangam Mitra": "bg-teal-100 text-teal-800",
  "Guest": "bg-green-100 text-green-800",
  "Sangam Mitra Guest": "bg-green-100 text-green-800",
  "Guruji": "bg-orange-100 text-orange-800",
  "Medical": "bg-red-100 text-red-800",
  "Rishta Samanvayak": "bg-pink-100 text-pink-800",
  "Sanyojak": "bg-indigo-100 text-indigo-800",
};

const STATUS_COLORS = {
  draft: "bg-gray-100 text-gray-600",
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
};

const GuestVolunteerManagement = () => {
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ status: '', category: '', search: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showCommentModal, setShowCommentModal] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [showEditModal, setShowEditModal] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const csvFileRef = React.useRef(null);

  const token = localStorage.getItem('token');
  const appUrl = window.location.origin;

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '30' });
      if (filters.status) params.append('status', filters.status);
      if (filters.category) params.append('category', filters.category);
      if (filters.search) params.append('search', filters.search);

      const res = await fetch(`${BACKEND_URL}/api/ps/guest-volunteer/admin/list?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRecords(data.records || []);
        setTotalPages(data.totalPages || 1);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, filters, token]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/ps/guest-volunteer/admin/stats/summary`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const updateStatus = async (regId, status, reason = '') => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/ps/guest-volunteer/admin/${regId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status, rejectionReason: reason }),
      });
      if (res.ok) {
        toast.success(`Status updated to ${status}`);
        fetchRecords();
        fetchStats();
        setShowRejectModal(null);
        setRejectionReason('');
      } else {
        toast.error('Failed to update status');
      }
    } catch (err) {
      toast.error('Error updating status');
    }
  };

  const saveComments = async (regId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/ps/guest-volunteer/admin/${regId}/comments`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ adminComments: commentText }),
      });
      if (res.ok) {
        toast.success('Comments saved');
        fetchRecords();
        setShowCommentModal(null);
        setCommentText('');
      }
    } catch (err) {
      toast.error('Error saving comments');
    }
  };

  const getCategoryShort = (cat) => {
    const map = {
      "Mahasabha Padadhikari": "Padadhikari",
      "Karyakarini Sadasya": "Karyakarini",
      "Samaj Adhyaksh": "Adhyaksh",
      "Mahila Swayamsevak": "M. Swayamsevak",
      "Swayamsevak": "Swayamsevak",
      "Sangam Mitra": "Sangam Mitra",
      "Guest": "Guest",
      "Sangam Mitra Guest": "SM Guest",
      "Guruji": "Guruji",
      "Medical": "Medical",
      "Rishta Samanvayak": "R. Samanvayak",
      "Sanyojak": "Sanyojak",
    };
    return map[cat] || cat;
  };

  const handleCsvImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/ps/guest-volunteer/admin/import-csv`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      if (res.ok) {
        const data = await res.json();
        setImportResult(data);
        toast.success(`Imported ${data.imported} records`);
        fetchRecords();
        fetchStats();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Import failed');
      }
    } catch { toast.error('Import error'); }
    finally { setImporting(false); }
  };

  const downloadIdCard = async (regId, memberNum) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/ps/guest-volunteer/admin/${regId}/idcard`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `IDCard_${memberNum}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to generate ID card');
      }
    } catch { toast.error('Error generating ID card'); }
  };

  const copyLink = (tk) => {
    const link = `${appUrl}/parichay-sangam/guest-volunteer/complete/${tk}`;
    navigator.clipboard.writeText(link).then(() => toast.success('Link copied!')).catch(() => toast.error('Copy failed'));
  };

  // Bulk Room/Seat upload
  const seatFileRef = React.useRef(null);
  const [showSeatUploadModal, setShowSeatUploadModal] = useState(false);
  const [seatUploading, setSeatUploading] = useState(false);
  const [seatUploadResult, setSeatUploadResult] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  const handleSeatRoomUpload = async (file) => {
    if (!file) return;
    setSeatUploading(true);
    setSeatUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/ps/guest-volunteer/admin/bulk-update-fields`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      if (res.ok) {
        const data = await res.json();
        setSeatUploadResult(data);
        toast.success(data.message || `Updated ${data.updated} records`);
        fetchRecords();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Upload failed');
      }
    } catch { toast.error('Upload error'); }
    finally { setSeatUploading(false); if (seatFileRef.current) seatFileRef.current.value = ''; }
  };

  const [regenProgress, setRegenProgress] = useState({ current: 0, total: 0 });
  const handleBulkRegenerate = async () => {
    if (!window.confirm(`Regenerate and send updated ID cards (with Room & Seat + timetable) to ALL approved Guest/Volunteer records via email?`)) return;
    setRegenerating(true);
    setRegenProgress({ current: 0, total: 0 });
    try {
      const countRes = await fetch(`${BACKEND_URL}/api/ps/guest-volunteer/admin/bulk-regenerate-idcards/count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!countRes.ok) throw new Error('Failed to get count');
      const countData = await countRes.json();
      const total = countData.total || 0;
      const batchSize = countData.batchSize || 20;
      if (total === 0) { toast.error('No approved records with email'); return; }
      const totalBatches = Math.ceil(total / batchSize);
      toast.success(`Sending ${total} emails in ${totalBatches} batch${totalBatches > 1 ? 'es' : ''}…`);
      let sentTotal = 0;
      let failedTotal = 0;
      for (let i = 0; i < totalBatches; i++) {
        const skip = i * batchSize;
        setRegenProgress({ current: i + 1, total: totalBatches });
        const res = await fetch(
          `${BACKEND_URL}/api/ps/guest-volunteer/admin/bulk-regenerate-idcards?skip=${skip}&limit=${batchSize}`,
          { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Batch ${i + 1} failed`);
        }
        const data = await res.json();
        sentTotal += data.sent || 0;
        failedTotal += data.failed || 0;
      }
      toast.success(`Done! Sent ${sentTotal} email${sentTotal !== 1 ? 's' : ''}${failedTotal > 0 ? `, ${failedTotal} failed` : ''}.`);
    } catch (err) {
      toast.error('Regenerate error: ' + err.message);
    } finally {
      setRegenerating(false);
      setRegenProgress({ current: 0, total: 0 });
    }
  };

  const [downloadingA3, setDownloadingA3] = useState(false);
  const [a3Progress, setA3Progress] = useState({ current: 0, total: 0 });
  const handleDownloadA3 = async () => {
    setDownloadingA3(true);
    setA3Progress({ current: 0, total: 0 });
    try {
      const countRes = await fetch(`${BACKEND_URL}/api/ps/guest-volunteer/admin/idcards/a3-sheet/count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!countRes.ok) throw new Error('Failed to get count');
      const countData = await countRes.json();
      const total = countData.total || 0;
      const batchSize = countData.batchSize || 60;
      if (total === 0) { toast.error('No approved guests/volunteers'); return; }
      const totalBatches = Math.ceil(total / batchSize);
      toast.success(`Generating ${totalBatches} A3 PDF${totalBatches > 1 ? 's' : ''} for ${total} cards…`);
      let downloaded = 0;
      for (let i = 0; i < totalBatches; i++) {
        const skip = i * batchSize;
        setA3Progress({ current: i + 1, total: totalBatches });
        const res = await fetch(
          `${BACKEND_URL}/api/ps/guest-volunteer/admin/idcards/a3-sheet?skip=${skip}&limit=${batchSize}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Batch ${i + 1} failed`);
        }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `PS2026_GuestsVolunteers_A3Sheets_part${i + 1}of${totalBatches}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        downloaded += 1;
        await new Promise((r) => setTimeout(r, 400));
      }
      toast.success(`Downloaded ${downloaded} A3 PDF file${downloaded > 1 ? 's' : ''}`);
    } catch (err) {
      toast.error('A3 sheet failed: ' + err.message);
    } finally {
      setDownloadingA3(false);
      setA3Progress({ current: 0, total: 0 });
    }
  };

  const downloadSeatTemplate = () => {
    // CSV template: Member No., Registration Code, Full Name (ref), Room No., Seat No.
    const header = ['Member No.', 'Registration Code', 'Full Name (reference only)', 'Room No.', 'Seat No.'];
    const sample = records.slice(0, 5).map(r => [
      r.memberNumber || '',
      r.registrationCode || '',
      r.fullName || '',
      r.roomNumber || '',
      r.seatNumber || ''
    ]);
    const csv = [header, ...sample].map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'GV_Seat_Room_Template.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const openEditModal = (rec) => {
    setEditData({
      fullName: rec.fullName || '',
      fatherOrHusbandName: rec.fatherOrHusbandName || '',
      nukh: rec.nukh || '',
      currentPlace: rec.currentPlace || '',
      whatsapp: rec.whatsapp || '',
      email: rec.email || '',
      category: rec.category || '',
      position: rec.position || '',
      roomNumber: rec.roomNumber || '',
      seatNumber: rec.seatNumber || '',
      paymentMode: rec.paymentMode || '',
      transactionId: rec.transactionId || '',
    });
    setShowEditModal(rec);
  };

  const saveEdit = async () => {
    if (!showEditModal) return;
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ps/guest-volunteer/admin/${showEditModal.id}/edit`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(editData),
      });
      if (res.ok) {
        toast.success('Record updated');
        fetchRecords();
        fetchStats();
        setShowEditModal(null);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Update failed');
      }
    } catch { toast.error('Error saving'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="gv-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Guest & Volunteer Management</h1>
          <p className="text-gray-500 text-sm">PS 2026 - Guests, Volunteers & Members</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={async () => {
            try {
              const params = new URLSearchParams();
              if (filters.status) params.append('status', filters.status);
              if (filters.category) params.append('category', filters.category);
              const res = await fetch(`${BACKEND_URL}/api/ps/guest-volunteer/admin/export-csv?${params}`, {
                headers: { 'Authorization': `Bearer ${token}` },
              });
              if (res.ok) {
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'GV_Registrations_Export.csv';
                a.click();
                URL.revokeObjectURL(url);
              } else { toast.error('Export failed'); }
            } catch { toast.error('Export error'); }
          }} data-testid="gv-export-csv-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button onClick={() => { setShowImportModal(true); setImportResult(null); }} className="bg-primary" data-testid="gv-import-csv-btn">
            <Upload className="w-4 h-4 mr-2" /> Import CSV
          </Button>
          <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50"
            onClick={() => { setShowSeatUploadModal(true); setSeatUploadResult(null); }}
            data-testid="gv-upload-seatroom-btn">
            <Upload className="w-4 h-4 mr-2" /> Upload Room/Seat
          </Button>
          <Button variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50"
            onClick={handleBulkRegenerate} disabled={regenerating}
            data-testid="gv-bulk-regenerate-btn">
            {regenerating
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {regenProgress.total > 0 ? `Sending ${regenProgress.current}/${regenProgress.total}…` : 'Starting…'}</>
              : <><FileText className="w-4 h-4 mr-2" /> Regenerate ID Cards</>
            }
          </Button>
          <Button variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            onClick={handleDownloadA3} disabled={downloadingA3}
            data-testid="gv-a3-sheet-btn">
            {downloadingA3
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {a3Progress.total > 0 ? `Downloading ${a3Progress.current}/${a3Progress.total}…` : 'Generating…'}</>
              : <><FileText className="w-4 h-4 mr-2" /> A3 Print Sheet</>
            }
          </Button>
          <Button variant="outline" className="border-green-300 text-green-700 hover:bg-green-50"
            onClick={async () => {
              if (!window.confirm(`Send approval emails with ID cards to all ${stats?.approved || 0} approved records?`)) return;
              try {
                const res = await fetch(`${BACKEND_URL}/api/ps/guest-volunteer/admin/bulk-send-emails`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}` },
                });
                if (res.ok) {
                  const data = await res.json();
                  toast.success(data.message);
                } else { toast.error('Failed to send emails'); }
              } catch { toast.error('Error sending emails'); }
            }} data-testid="gv-bulk-email-btn">
            <Mail className="w-4 h-4 mr-2" /> Send Approval Emails
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-xs text-gray-500">Total</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-400">{stats.draft || 0}</p>
            <p className="text-xs text-gray-500">Draft</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
            <p className="text-xs text-gray-500">Pending</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
            <p className="text-xs text-gray-500">Approved</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
            <p className="text-xs text-gray-500">Rejected</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-primary">{'\u20B9'}{(stats.totalFee || 0).toLocaleString()}</p>
            <p className="text-xs text-gray-500">Total Fee</p>
          </Card>
        </div>
      )}

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <Input
            placeholder="Search by name, email, code, phone..."
            value={filters.search}
            onChange={(e) => { setFilters(prev => ({ ...prev, search: e.target.value })); setPage(1); }}
            className="pl-10"
            data-testid="gv-admin-search"
          />
        </div>
        <select
          value={filters.status}
          onChange={(e) => { setFilters(prev => ({ ...prev, status: e.target.value })); setPage(1); }}
          className="h-10 px-3 rounded-md border text-sm"
          data-testid="gv-admin-status-filter"
        >
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <select
          value={filters.category}
          onChange={(e) => { setFilters(prev => ({ ...prev, category: e.target.value })); setPage(1); }}
          className="h-10 px-3 rounded-md border text-sm"
          data-testid="gv-admin-category-filter"
        >
          <option value="">All Categories</option>
          <option value="Mahasabha Padadhikari">Mahasabha Padadhikari</option>
          <option value="Karyakarini Sadasya">Karyakarini Sadasya</option>
          <option value="Samaj Adhyaksh">Samaj Adhyaksh</option>
          <option value="Mahila Swayamsevak">Mahila Swayamsevak</option>
          <option value="Swayamsevak">Swayamsevak</option>
          <option value="Sangam Mitra">Sangam Mitra</option>
          <option value="Guest">Guest</option>
          <option value="Sangam Mitra Guest">Sangam Mitra Guest</option>
          <option value="Guruji">Guruji</option>
          <option value="Medical">Medical</option>
          <option value="Rishta Samanvayak">Rishta Samanvayak</option>
          <option value="Sanyojak">Sanyojak</option>
        </select>
      </div>

      <p className="text-sm text-gray-500">Showing {records.length} of {total} registrations</p>

      {/* Records List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : records.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No registrations found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {records.map((rec) => (
            <Card key={rec.id} className="overflow-hidden">
              {/* Summary Row */}
              <div
                className="p-4 flex items-center gap-4 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedId(expandedId === rec.id ? null : rec.id)}
                data-testid={`gv-record-${rec.registrationCode}`}
              >
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {rec.profilePhotoUrl ? (
                    <img src={resolveFileUrl(rec.profilePhotoUrl)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-5 h-5 text-gray-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900">{[rec.fullName, rec.fatherOrHusbandName, rec.nukh].filter(Boolean).join(' ')}</span>
                    {rec.memberNumber && (
                      <span className="text-xs font-bold font-mono bg-primary/10 text-primary px-2 py-0.5 rounded" data-testid={`gv-member-${rec.registrationCode}`}>
                        {rec.memberNumber}
                      </span>
                    )}
                    <span className="text-xs font-mono text-gray-400">{rec.registrationCode}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[rec.category] || 'bg-gray-100'}`}>
                      {getCategoryShort(rec.category)}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[rec.status] || ''}`}>
                      {rec.status}
                    </span>
                    {rec.fee > 0 && <span className="text-xs text-gray-500">{'\u20B9'}{rec.fee}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {rec.status === 'pending' && (
                    <>
                      <Button size="sm" variant="outline" className="text-green-600 border-green-300 hover:bg-green-50 h-8"
                        onClick={(e) => { e.stopPropagation(); updateStatus(rec.id, 'approved'); }}
                        data-testid={`gv-approve-${rec.registrationCode}`}>
                        <CheckCircle className="w-3 h-3 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50 h-8"
                        onClick={(e) => { e.stopPropagation(); setShowRejectModal(rec); setRejectionReason(''); }}
                        data-testid={`gv-reject-${rec.registrationCode}`}>
                        <XCircle className="w-3 h-3 mr-1" /> Reject
                      </Button>
                    </>
                  )}
                  {rec.status === 'approved' && (
                    <Button size="sm" variant="outline" className="text-yellow-600 border-yellow-300 hover:bg-yellow-50 h-8"
                      onClick={(e) => { e.stopPropagation(); updateStatus(rec.id, 'pending'); }}>
                      <Clock className="w-3 h-3 mr-1" /> Revert
                    </Button>
                  )}
                  {rec.status === 'rejected' && (
                    <Button size="sm" variant="outline" className="text-green-600 border-green-300 hover:bg-green-50 h-8"
                      onClick={(e) => { e.stopPropagation(); updateStatus(rec.id, 'approved'); }}>
                      <CheckCircle className="w-3 h-3 mr-1" /> Approve
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-8 text-blue-600" title="Edit record"
                    onClick={(e) => { e.stopPropagation(); openEditModal(rec); }}
                    data-testid={`gv-edit-${rec.registrationCode}`}>
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8"
                    onClick={(e) => { e.stopPropagation(); setShowCommentModal(rec); setCommentText(rec.adminComments || ''); }}
                    data-testid={`gv-comment-${rec.registrationCode}`}>
                    <MessageSquare className="w-3 h-3" />
                  </Button>
                  {rec.completionToken && (
                    <Button size="sm" variant="ghost" className="h-8 text-blue-600" title="Copy form link"
                      onClick={(e) => { e.stopPropagation(); copyLink(rec.completionToken); }}>
                      <Link className="w-3 h-3" />
                    </Button>
                  )}
                  {rec.status === 'approved' && (
                    <Button size="sm" variant="ghost" className="h-8 text-green-600" title="Download ID Card"
                      onClick={(e) => { e.stopPropagation(); downloadIdCard(rec.id, rec.memberNumber || rec.registrationCode); }}
                      data-testid={`gv-idcard-${rec.registrationCode}`}>
                      <Download className="w-3 h-3" />
                    </Button>
                  )}
                  {expandedId === rec.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>

              {/* Expanded Detail */}
              <AnimatePresence>
                {expandedId === rec.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t bg-gray-50"
                  >
                    <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Photo */}
                      <div className="flex flex-col items-center">
                        {rec.profilePhotoUrl && (
                          <img src={resolveFileUrl(rec.profilePhotoUrl)} alt={rec.fullName} className="w-32 h-40 object-cover rounded-lg border shadow-sm mb-2" />
                        )}
                        <p className="text-xs text-gray-500 mt-1">Profile Photo</p>
                      </div>

                      {/* Personal Details */}
                      <div className="space-y-2 text-sm">
                        <h4 className="font-semibold text-gray-700 text-xs uppercase tracking-wider mb-2">Personal Details</h4>
                        {rec.memberNumber && (
                          <p><span className="text-gray-500">Member No.:</span> <strong className="text-primary font-mono">{rec.memberNumber}</strong></p>
                        )}
                        <p><span className="text-gray-500">Registration Code:</span> <span className="font-mono">{rec.registrationCode}</span></p>
                        <p><span className="text-gray-500">Name:</span> <strong>{rec.fullName}</strong></p>
                        <p><span className="text-gray-500">Father/Husband:</span> {rec.fatherOrHusbandName}</p>
                        <p><span className="text-gray-500">Nukh:</span> {rec.nukh || '-'}</p>
                        <p><span className="text-gray-500">Place:</span> {rec.currentPlace}</p>
                        <p><span className="text-gray-500">WhatsApp:</span> {rec.whatsapp}</p>
                        <p><span className="text-gray-500">Email:</span> {rec.email}</p>
                        {(rec.roomNumber || rec.seatNumber) && (
                          <div className="mt-2 p-2 bg-purple-50 rounded border border-purple-200 text-xs">
                            {rec.roomNumber && <p><span className="text-purple-700">Room No.:</span> <strong>{rec.roomNumber}</strong></p>}
                            {rec.seatNumber && <p><span className="text-purple-700">Seat No.:</span> <strong>{rec.seatNumber}</strong></p>}
                          </div>
                        )}
                      </div>

                      {/* Category-specific & Payment */}
                      <div className="space-y-2 text-sm">
                        <h4 className="font-semibold text-gray-700 text-xs uppercase tracking-wider mb-2">Category Details</h4>
                        <p><span className="text-gray-500">Category:</span> <strong>{rec.category}</strong></p>
                        <p><span className="text-gray-500">Fee:</span> {rec.fee > 0 ? `₹${rec.fee}` : 'Free'}</p>

                        {/* Position - editable inline */}
                        <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                          <label className="text-xs font-medium text-blue-700 block mb-1">Position (for ID Card)</label>
                          <div className="flex gap-2 items-center">
                            <input
                              type="text"
                              id={`gv-pos-input-${rec.id}`}
                              defaultValue={rec.position || ''}
                              placeholder="e.g. President, Coordinator"
                              className="flex-1 h-8 px-2 text-sm border rounded"
                              data-testid={`gv-position-${rec.registrationCode}`}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const val = e.target.value;
                                  fetch(`${BACKEND_URL}/api/ps/guest-volunteer/admin/${rec.id}/position`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                    body: JSON.stringify({ position: val }),
                                  }).then(r => { if (r.ok) { toast.success('Position saved'); fetchRecords(); } else toast.error('Failed to save position'); });
                                }
                              }}
                            />
                            <Button size="sm" variant="outline" className="h-8 text-xs"
                              onClick={() => {
                                const input = document.getElementById(`gv-pos-input-${rec.id}`);
                                const val = input ? input.value : '';
                                fetch(`${BACKEND_URL}/api/ps/guest-volunteer/admin/${rec.id}/position`, {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                  body: JSON.stringify({ position: val }),
                                }).then(r => { if (r.ok) { toast.success('Position saved'); fetchRecords(); } else toast.error('Failed to save position'); });
                              }}>Save</Button>
                          </div>
                          {rec.position && <p className="text-xs text-blue-600 mt-1">Current: <strong>{rec.position}</strong></p>}
                        </div>

                        {/* Leadership */}
                        {rec.localUnit && <p><span className="text-gray-500">Local Unit:</span> {rec.localUnit}</p>}
                        {rec.designation && <p><span className="text-gray-500">Designation:</span> {rec.designation}</p>}

                        {/* Volunteer */}
                        {rec.tshirtSize && <p><span className="text-gray-500">T-Shirt:</span> {rec.tshirtSize}</p>}
                        {rec.preferredDepartment && <p><span className="text-gray-500">Department:</span> {rec.preferredDepartment}</p>}
                        {rec.availability?.length > 0 && <p><span className="text-gray-500">Availability:</span> {rec.availability.join(', ')}</p>}

                        {/* Guest */}
                        {rec.organization && <p><span className="text-gray-500">Organization:</span> {rec.organization}</p>}
                        {rec.guestDesignation && <p><span className="text-gray-500">Guest Designation:</span> {rec.guestDesignation}</p>}

                        {/* Payment */}
                        {rec.paymentMode && <p><span className="text-gray-500">Payment Mode:</span> {rec.paymentMode}</p>}
                        {rec.transactionId && <p><span className="text-gray-500">Transaction ID:</span> <span className="font-mono">{rec.transactionId}</span></p>}
                        {rec.paymentProofUrl && (
                          <div className="mt-2">
                            <p className="text-gray-500 mb-1">Payment Proof:</p>
                            <a href={resolveFileUrl(rec.paymentProofUrl)} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-xs">View Payment Screenshot</a>
                          </div>
                        )}

                        {rec.rejectionReason && (
                          <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                            <p className="text-xs text-red-700"><strong>Rejection Reason:</strong> {rec.rejectionReason}</p>
                          </div>
                        )}
                        {rec.adminComments && (
                          <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                            <p className="text-xs text-amber-700"><strong>Admin Comments:</strong> {rec.adminComments}</p>
                          </div>
                        )}

                        <p className="text-xs text-gray-400 mt-2">Registered: {formatDisplayDate(rec.createdAt)}</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <PaginationBar
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(p) => setPage(p)}
          itemLabel="records"
          testIdPrefix="gv-admin"
        />
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Reject Registration</h3>
            <p className="text-sm text-gray-600 mb-3">Rejecting: <strong>{showRejectModal.fullName}</strong> ({showRejectModal.category})</p>
            <Label className="text-sm">Rejection Reason</Label>
            <Textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} rows={3} placeholder="Enter reason for rejection..." className="mt-1 mb-4" data-testid="gv-rejection-reason" />
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowRejectModal(null)}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700" onClick={() => updateStatus(showRejectModal.id, 'rejected', rejectionReason)} data-testid="gv-confirm-reject">
                Reject
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Comment Modal */}
      {showCommentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Admin Comments</h3>
            <p className="text-sm text-gray-600 mb-3">For: <strong>{showCommentModal.fullName}</strong></p>
            <Textarea value={commentText} onChange={(e) => setCommentText(e.target.value)} rows={4} placeholder="Enter comments/feedback..." className="mb-4" data-testid="gv-admin-comments" />
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowCommentModal(null)}>Cancel</Button>
              <Button className="bg-primary" onClick={() => saveComments(showCommentModal.id)} data-testid="gv-save-comments">
                Save Comments
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Import from CSV</h3>
              <button onClick={() => setShowImportModal(false)} className="p-2 hover:bg-gray-100 rounded"><X size={18} /></button>
            </div>

            {!importResult ? (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Upload a CSV file exported from the Google Sheet. Required columns: <strong>Category, Name (first), Father's / Husband's Name, Nukh, Current Place, Mobile Number, Room No., Seat No</strong>
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  Rows with unknown categories (e.g. Mahasabha Patron, Karkyakarta) will be skipped. Member numbers (M-1, M-2...) are auto-assigned.
                </p>
                <div className="flex items-center gap-4">
                  <Button onClick={() => csvFileRef.current?.click()} disabled={importing} variant="outline" className="border-dashed">
                    {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                    {importing ? 'Importing...' : 'Select CSV File'}
                  </Button>
                  <input ref={csvFileRef} type="file" accept=".csv" className="hidden"
                    onChange={(e) => { if (e.target.files[0]) handleCsvImport(e.target.files[0]); }} />
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-3 mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-semibold text-green-800">{importResult.imported} records imported</p>
                    {importResult.skipped > 0 && <p className="text-sm text-orange-700">{importResult.skipped} skipped</p>}
                  </div>
                </div>

                {importResult.skippedRecords?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Skipped Records:</p>
                    <div className="max-h-32 overflow-y-auto bg-gray-50 rounded p-2 text-xs space-y-1">
                      {importResult.skippedRecords.map((s, i) => (
                        <p key={i} className="text-gray-600">{s.name || 'No name'} — {s.category} ({s.reason})</p>
                      ))}
                    </div>
                  </div>
                )}

                {importResult.records?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Imported Records & Form Links:</p>
                    <div className="max-h-60 overflow-y-auto border rounded">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="text-left p-2">Member</th>
                            <th className="text-left p-2">Name</th>
                            <th className="text-left p-2">Category</th>
                            <th className="text-left p-2">Link</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importResult.records.map((r, i) => (
                            <tr key={i} className="border-t">
                              <td className="p-2 font-mono">{r.memberNumber}</td>
                              <td className="p-2">{r.fullName}</td>
                              <td className="p-2">{r.category}</td>
                              <td className="p-2">
                                <button onClick={() => copyLink(r.token)} className="text-primary hover:underline flex items-center gap-1">
                                  <Copy className="w-3 h-3" /> Copy
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="flex justify-end mt-4">
                  <Button onClick={() => setShowImportModal(false)}>Done</Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Seat/Room Upload Modal */}
      {showSeatUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Upload Room / Seat Numbers</h3>
              <button onClick={() => setShowSeatUploadModal(false)} className="p-2 hover:bg-gray-100 rounded" data-testid="gv-seatupload-close-btn"><X size={18} /></button>
            </div>

            {!seatUploadResult ? (
              <div>
                <p className="text-sm text-gray-600 mb-3">
                  Upload an Excel (.xlsx) or CSV file with the following columns: <strong>Member No., Registration Code, Room No., Seat No.</strong> Either <strong>Member No.</strong> or <strong>Registration Code</strong> is required to match each record.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4 text-xs text-blue-900">
                  <p className="font-medium mb-1">Tips:</p>
                  <ul className="list-disc ml-4 space-y-0.5">
                    <li>Download the template below to get current data pre-filled.</li>
                    <li>Rows with both fields blank are skipped.</li>
                    <li>After upload, click "Regenerate ID Cards" to email updated cards to all approved records.</li>
                  </ul>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" onClick={downloadSeatTemplate} data-testid="gv-seatroom-template-btn">
                    <Download className="w-4 h-4 mr-2" /> Download Template
                  </Button>
                  <Button onClick={() => seatFileRef.current?.click()} disabled={seatUploading} className="bg-blue-600 hover:bg-blue-700" data-testid="gv-seatroom-upload-select-btn">
                    {seatUploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Select Excel / CSV</>}
                  </Button>
                  <input ref={seatFileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
                    onChange={(e) => { if (e.target.files[0]) handleSeatRoomUpload(e.target.files[0]); }}
                    data-testid="gv-seatroom-file-input" />
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-3 mb-4 p-3 bg-green-50 rounded-lg border border-green-200" data-testid="gv-seatroom-upload-result">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-semibold text-green-800">{seatUploadResult.updated} records updated</p>
                    {seatUploadResult.skipped > 0 && <p className="text-sm text-orange-700">{seatUploadResult.skipped} rows skipped (empty or no match)</p>}
                  </div>
                </div>

                {seatUploadResult.errors?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Errors:</p>
                    <div className="max-h-32 overflow-y-auto bg-red-50 border border-red-200 rounded p-2 text-xs space-y-1">
                      {seatUploadResult.errors.map((err, i) => (
                        <p key={i} className="text-red-700">{err.identifier}: {err.reason}</p>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-between gap-3 mt-4">
                  <Button variant="outline" onClick={() => { setSeatUploadResult(null); }}>Upload Another</Button>
                  <div className="flex gap-3">
                    <Button onClick={() => setShowSeatUploadModal(false)}>Done</Button>
                    <Button
                      onClick={() => { setShowSeatUploadModal(false); handleBulkRegenerate(); }}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="gv-seatroom-regenerate-btn"
                    >
                      <FileText className="w-4 h-4 mr-2" /> Regenerate ID Cards
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Edit Record</h3>
              <button onClick={() => setShowEditModal(null)} className="p-2 hover:bg-gray-100 rounded"><X size={18} /></button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              {showEditModal.registrationCode} — {showEditModal.memberNumber || ''}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Full Name <span className="text-destructive">*</span></Label>
                <Input value={editData.fullName} onChange={(e) => setEditData(d => ({ ...d, fullName: e.target.value }))} className="h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Father's / Husband's Name</Label>
                <Input value={editData.fatherOrHusbandName} onChange={(e) => setEditData(d => ({ ...d, fatherOrHusbandName: e.target.value }))} className="h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Nukh</Label>
                <Input value={editData.nukh} onChange={(e) => setEditData(d => ({ ...d, nukh: e.target.value }))} className="h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Current Place</Label>
                <Input value={editData.currentPlace} onChange={(e) => setEditData(d => ({ ...d, currentPlace: e.target.value }))} className="h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs">WhatsApp</Label>
                <Input value={editData.whatsapp} onChange={(e) => setEditData(d => ({ ...d, whatsapp: e.target.value }))} className="h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" value={editData.email} onChange={(e) => setEditData(d => ({ ...d, email: e.target.value }))} className="h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Category</Label>
                <select value={editData.category} onChange={(e) => setEditData(d => ({ ...d, category: e.target.value }))} className="h-9 w-full px-3 rounded-md border text-sm">
                  <option value="Mahasabha Padadhikari">Mahasabha Padadhikari</option>
                  <option value="Karyakarini Sadasya">Karyakarini Sadasya</option>
                  <option value="Samaj Adhyaksh">Samaj Adhyaksh</option>
                  <option value="Mahila Swayamsevak">Mahila Swayamsevak</option>
                  <option value="Swayamsevak">Swayamsevak</option>
                  <option value="Sangam Mitra">Sangam Mitra</option>
                  <option value="Guest">Guest</option>
                  <option value="Sangam Mitra Guest">Sangam Mitra Guest</option>
                  <option value="Guruji">Guruji</option>
                  <option value="Medical">Medical</option>
                  <option value="Rishta Samanvayak">Rishta Samanvayak</option>
                  <option value="Sanyojak">Sanyojak</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Position (for ID Card)</Label>
                <Input value={editData.position} onChange={(e) => setEditData(d => ({ ...d, position: e.target.value }))} className="h-9 text-sm" placeholder="e.g. President, Coordinator" />
              </div>
              <div>
                <Label className="text-xs">Room No.</Label>
                <Input value={editData.roomNumber} onChange={(e) => setEditData(d => ({ ...d, roomNumber: e.target.value }))} className="h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Seat No.</Label>
                <Input value={editData.seatNumber} onChange={(e) => setEditData(d => ({ ...d, seatNumber: e.target.value }))} className="h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Payment Mode</Label>
                <select value={editData.paymentMode} onChange={(e) => setEditData(d => ({ ...d, paymentMode: e.target.value }))} className="h-9 w-full px-3 rounded-md border text-sm">
                  <option value="">None</option>
                  <option value="UPI">UPI</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Transaction ID</Label>
                <Input value={editData.transactionId} onChange={(e) => setEditData(d => ({ ...d, transactionId: e.target.value }))} className="h-9 text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowEditModal(null)}>Cancel</Button>
              <Button className="bg-primary" onClick={saveEdit} disabled={saving} data-testid="gv-save-edit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Save Changes
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default GuestVolunteerManagement;
